import "@modernary/mui/dist/style.css";
import { createApp } from "vue";
import "./styles/tailwind.css";
// import "@modernary/ui-components/style.css";
import { apiHosts } from "@/api/constants/apiHosts";
import { LE } from "@/constants/enums";
import { createProPlugin, inputs } from "@formkit/pro";
import { defaultConfig, plugin } from "@formkit/vue";
import { default as mui } from "@modernary/mui";
import { useAppSettingsStore } from "@modernary/mui/components/common";
import { bffAuthPlugin, type BffOptions } from "@modernary/mui/utils/authentication";
import { authzPlugin, type AuthorizationClientConfig } from "@modernary/mui/utils/authorization";
import { useGeneratedDocumentConfigStore } from "@modernary/mui/utils/docGen";
import { useIdentityConfigStore } from "@modernary/mui/utils/identity";
import { createPinia } from "pinia";
import InfiniteLoading from "v3-infinite-loading";
import { createRouter, createWebHistory } from "vue-router";
import App from "./App.vue";
import { routeNames, routes } from "./routes";

import { formKitConfig } from "@/formkit.config";
import { autoAnimatePlugin } from "@formkit/auto-animate/vue";
import { useEntityDocumentConfigStore } from "@modernary/mui/components/document";
import { getLookupsFromLookupGroup } from "@modernary/mui/components/lookup";
import { useEntityNoteConfigStore } from "@modernary/mui/components/note";
import { useToastStore } from "@modernary/mui/components/toasts";

// TODO: Get import from ui-c working.

const app = createApp(App);

/**
 * Specify plugins to load from the /plugins directory
 * Pass file names without any extension like so
 *
 * @example
 *
 * loadPlugins(['vue-fontawesome'])
 */
// loadPlugins([]);

/**
 * Automatically imports and registers base components
 * Make sure you have at least one Base components in components/base directory
 */
// registerBaseComponents(app);

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
});

router.afterEach((to, from) => {
  const appSettingsStore = useAppSettingsStore();
  if (to.meta.title) {
    document.title = appSettingsStore.pageTitleBase + " - " + to.meta.title;
  } else {
    document.title = appSettingsStore.pageTitleBase;
  }
});

/* TODO: Consider changing to use https://router.vuejs.org/guide/advanced/navigation-guards.html#Global-Resolve-Guards
 Then we can await the lookups and inject them into the route for use in the component's props. */
router.beforeEach((to, from) => {
  if (to.meta.lookupGroups && Array.isArray(to.meta.lookupGroups)) {
    to.meta.lookupGroups.forEach(async (groupCode) => {
      getLookupsFromLookupGroup(groupCode);
    });
  }
});

// Register 3rd-party components used in ui-components
app.component("infinite-loading", InfiniteLoading);

const pro = createProPlugin("fk-53688fd8db", inputs);
formKitConfig.plugins?.push(pro);
if (formKitConfig.icons)
  formKitConfig.icons.checkCircleIcon = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-5 h-5"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clip-rule="evenodd" /></svg>`;

const pinia = createPinia();
app.use(pinia);
// import pinia plugins.

// App Settings - This must happen after we've created Pinia, and called app.use
const appSettingsStore = useAppSettingsStore();
appSettingsStore.agencyName = "AQMS";
appSettingsStore.pageTitleBase = "AQMS";
appSettingsStore.baseURL = import.meta.env.VITE_AQMS_API_BASE_URL;
appSettingsStore.apiHost = apiHosts.AQMS;
appSettingsStore.systemIdentifier = "AQMS2";
appSettingsStore.applicationTitle = "Air Quality Management System";
appSettingsStore.viteBaseUrl = import.meta.env.BASE_URL;
appSettingsStore.datepickerFormat = "MM/DD/YYYY \\at hh:mm A";
appSettingsStore.ianaSystemTimeZone = "America/Los_Angeles";
appSettingsStore.timeZoneMap = new Map([
  ["GMT-8", "PST"],
  ["GMT-7", "PDT"],
]);
appSettingsStore.defaultAddressCountryMurn = LE.Country.USA;
appSettingsStore.displayTimeZones = true;
appSettingsStore.changePasswordUrl = import.meta.env.VITE_IDADMIN_CHANGE_PASSWORD_URL;
appSettingsStore.includeZipCountryCommaInAddress = true;

const identityConfigStore = useIdentityConfigStore();
identityConfigStore.setBaseURL(import.meta.env.VITE_IDADMIN_API_BASE_URL);
identityConfigStore.setApiHost(apiHosts.IDADMIN);

const generatedDocumentConfigStore = useGeneratedDocumentConfigStore();
generatedDocumentConfigStore.setBaseURL(import.meta.env.VITE_DOCGEN_API_BASE_URL);
generatedDocumentConfigStore.setApiHost(apiHosts.DOCGEN);

const entityNoteConfigStore = useEntityNoteConfigStore();
entityNoteConfigStore.noteMaxLength = 0;
entityNoteConfigStore.enableType = true;
entityNoteConfigStore.enableAudience = true;

const entityDocumentConfigStore = useEntityDocumentConfigStore();
entityDocumentConfigStore.undownloadableMessage =
  "This file upload is not complete. Please refresh to update the file attachment status. Contact support if the file does not upload.";
entityDocumentConfigStore.prefersDownloading = false;
entityDocumentConfigStore.documentStatuses = { pending: 0, uploaded: 1, failed: 2 };

entityDocumentConfigStore.entityDocumentMap = new Map([
  [
    "asbestosEnforcementAction",
    {
      config: {
        routeNamePrefix: "AsbestosEnforcementAction.AsbestosEnforcementActionDocuments",
        categoryMurn: LE.DocumentCategory.EnforcementAction,
        listName: routeNames.asbestosEnforcementAction.documents,
      },
      onlyInFormEdit: false,
      murnResourceType: "enforcementAction",
    },
  ],
  [
    "asbestosFacility",
    {
      config: {
        routeNamePrefix: "AsbestosFacility.AsbestosFacilityDocuments",
        categoryMurn: LE.DocumentCategory.AsbestosFacility,
        listName: routeNames.asbestosFacility.documents,
      },
      onlyInFormEdit: false,
      murnResourceType: "asbestosFacility",
    },
  ],
  [
    "asbestosProject",
    {
      config: {
        routeNamePrefix: "AsbestosProject.AsbestosProjectDocuments",
        categoryMurn: LE.DocumentCategory.AsbestosProject,
        listName: routeNames.asbestosProject.documents,
      },
      onlyInFormEdit: false,
      murnResourceType: "asbestosProject",
    },
  ],
  [
    "dustClass",
    {
      config: {
        routeNamePrefix: "Dust.DustClassDocuments",
        categoryMurn: LE.DocumentCategory.DustClass,
        listName: routeNames.dust.documents,
      },
      onlyInFormEdit: false,
      murnResourceType: "dustClass",
    },
  ],
  [
    "asbestosInspection",
    {
      config: {
        routeNamePrefix: "AsbestosInspection.AsbestosInspectionDocuments",
        categoryMurn: LE.DocumentCategory.Inspection,
        listName: routeNames.asbestosInspection.documents,
      },
      onlyInFormEdit: false,
      murnResourceType: "inspection",
    },
  ],
  //
  [
    "complaint",
    {
      config: {
        routeNamePrefix: "Complaint.ComplaintDocuments",
        categoryMurn: LE.DocumentCategory.Complaint,
        listName: routeNames.complaint.documents,
      },
      onlyInFormEdit: false,
      murnResourceType: "complaint",
    },
  ],
  [
    "complaintEnforcementAction",
    {
      config: {
        routeNamePrefix: "ComplaintEnforcementAction.ComplaintEnforcementActionDocuments",
        categoryMurn: LE.DocumentCategory.EnforcementAction,
        listName: routeNames.complaintEnforcementAction.documents,
      },
      onlyInFormEdit: false,
      murnResourceType: "enforcementAction",
    },
  ],
  [
    "complaintInvestigation",
    {
      config: {
        routeNamePrefix: "ComplaintInvestigation.ComplaintInvestigationDocuments",
        categoryMurn: LE.DocumentCategory.Investigation,
        listName: routeNames.complaintInvestigation.documents,
      },
      onlyInFormEdit: false,
      murnResourceType: "inspection",
    },
  ],
  [
    "constructionProject",
    {
      config: {
        routeNamePrefix: "ConstructionProject.ConstructionProjectDocuments",
        categoryMurn: LE.DocumentCategory.ConstructionProject,
        listName: routeNames.constructionProject.documents,
      },
      onlyInFormEdit: false,
      murnResourceType: "constructionProject",
    },
  ],
  [
    "dustEnforcementAction",
    {
      config: {
        routeNamePrefix: "DustEnforcementAction.DustEnforcementActionDocuments",
        categoryMurn: LE.DocumentCategory.EnforcementAction,
        listName: routeNames.dustEnforcementAction.documents,
      },
      onlyInFormEdit: false,
      murnResourceType: "enforcementAction",
    },
  ],
  [
    "dustInspection",
    {
      config: {
        routeNamePrefix: "DustInspection.DustInspectionDocuments",
        categoryMurn: LE.DocumentCategory.Inspection,
        listName: routeNames.dustInspection.documents,
      },
      onlyInFormEdit: false,
      murnResourceType: "inspection",
    },
  ],

  [
    "stationarySource",
    {
      config: {
        routeNamePrefix: "StationarySource.StationarySourceDocuments",
        categoryMurn: LE.DocumentCategory.StationarySource,
        listName: routeNames.stationarySource.documents,
      },
      onlyInFormEdit: false,
      murnResourceType: "stationarySource",
    },
  ],
  [
    "stationarySourceEnforcementAction",
    {
      config: {
        routeNamePrefix: "StationarySourceEnforcementAction.StationarySourceEnforcementActionDocuments",
        categoryMurn: LE.DocumentCategory.EnforcementAction,
        listName: routeNames.stationarySourceEnforcementAction.documents,
      },
      onlyInFormEdit: false,
      murnResourceType: "enforcementAction",
    },
  ],
  [
    "vacantLandEnforcementAction",
    {
      config: {
        routeNamePrefix: "VacantLandEnforcementAction.VacantLandEnforcementActionDocuments",
        categoryMurn: LE.DocumentCategory.EnforcementAction,
        listName: routeNames.vacantLandEnforcementAction.documents,
      },
      onlyInFormEdit: false,
      murnResourceType: "enforcementAction",
    },
  ],
  [
    "vacantLandInspection",
    {
      config: {
        routeNamePrefix: "VacantLandInspection.VacantLandInspectionDocuments",
        categoryMurn: LE.DocumentCategory.Inspection,
        listName: routeNames.vacantLandEnforcementAction.documents,
      },
      onlyInFormEdit: false,
      murnResourceType: "inspection",
    },
  ],
  [
    "electronicForm",
    {
      onlyInFormEdit: true,
      murnResourceType: "electronicForm",
    },
  ],
]);

const toastStore = useToastStore();
toastStore.setup(app);

app.use(router);
app.use(plugin, defaultConfig(formKitConfig));
app.use(authzPlugin, {
  baseApiUrl: import.meta.env.VITE_AUTHORIZATION_API_BASE_URL,
} as AuthorizationClientConfig);
app.use(mui);
app.use(bffAuthPlugin, {
  registerUrl: import.meta.env.VITE_IDADMIN_REGISTER_URL,
} as BffOptions);
app.use(autoAnimatePlugin);
app.mount("#app");
